import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";

const Req = () => (
  <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>
);

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone_number: "",
    street_address: "",
    postal_code: "",
    city: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const address = [
        form.street_address.trim(),
        form.postal_code.trim(),
        form.city.trim(),
      ]
        .filter(Boolean)
        .join(", ");
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        phone_number: form.phone_number,
        address,
      });
      toast.success("Konto opprettet");
      nav("/butikk");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="tk-container py-20 max-w-lg" data-testid="register-page">
      <p className="tk-overline mb-3">Ny konto</p>
      <h1 className="text-3xl font-semibold mb-2">Opprett kundekonto</h1>
      <p className="text-sm text-slate-500 mb-8">
        Du trenger en konto for å bestille reparasjon og spore bestillinger.
        Felt merket med <span className="text-red-500">*</span> er obligatoriske.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700">
            Fullt navn<Req />
          </label>
          <input
            required
            className="tk-input mt-1"
            value={form.name}
            onChange={set("name")}
            data-testid="register-name-input"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">
            E-post<Req />
          </label>
          <input
            required
            type="email"
            className="tk-input mt-1"
            value={form.email}
            onChange={set("email")}
            data-testid="register-email-input"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">
            Passord<Req />
          </label>
          <input
            required
            type="password"
            minLength={6}
            className="tk-input mt-1"
            value={form.password}
            onChange={set("password")}
            data-testid="register-password-input"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">
            Telefonnummer<Req />
          </label>
          <input
            required
            className="tk-input mt-1"
            placeholder="+47 …"
            value={form.phone_number}
            onChange={set("phone_number")}
            data-testid="register-phone-input"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">
            Adresse<Req />
          </label>
          <input
            required
            className="tk-input mt-1"
            placeholder="Storgata 12"
            value={form.street_address}
            onChange={set("street_address")}
            data-testid="register-address-input"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-1">
            <label className="text-sm font-medium text-slate-700">
              Postnummer<Req />
            </label>
            <input
              required
              inputMode="numeric"
              pattern="[0-9]{4}"
              maxLength={4}
              className="tk-input mt-1"
              placeholder="0184"
              value={form.postal_code}
              onChange={set("postal_code")}
              data-testid="register-postal-input"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-slate-700">
              By<Req />
            </label>
            <input
              required
              className="tk-input mt-1"
              placeholder="Oslo"
              value={form.city}
              onChange={set("city")}
              data-testid="register-city-input"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={busy}
          className="tk-btn-primary w-full justify-center"
          data-testid="register-submit-btn"
        >
          {busy ? "Oppretter …" : "Opprett konto"}
        </button>
      </form>
      <p className="text-sm text-slate-500 mt-6">
        Har du allerede konto?{" "}
        <Link
          to="/logg-inn"
          className="text-[#0B1C30] font-medium underline"
          data-testid="goto-login-link"
        >
          Logg inn
        </Link>
      </p>
    </div>
  );
}
